import { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, MailCheck } from 'lucide-react';
import { ApiClientError, LogoMark, Spinner } from '@riskpay/ui-components';
import { authApi } from '../lib/client';

/* ==========================================================================
 *  Password reset
 *
 *  Two steps against the OTP endpoints. The first response is identical
 *  whether or not the address is registered, so this screen advances to step
 *  two either way — showing "no account with that email" here would turn the
 *  form into an account-enumeration oracle, which is exactly what the API
 *  goes out of its way to prevent.
 * ========================================================================== */

const RULES = [
  { label: 'At least 12 characters', test: (v: string) => v.length >= 12 },
  { label: 'An uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'A lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'A digit', test: (v: string) => /\d/.test(v) },
];

export function ForgotPassword({ onNavigate }: { onNavigate: (to: 'signin') => void }) {
  const [step, setStep] = useState<'request' | 'reset' | 'done'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);

  const checks = useMemo(() => RULES.map((r) => ({ label: r.label, ok: r.test(password) })), [password]);
  const passwordOk = checks.every((c) => c.ok);

  async function request(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.post<{ dev_code?: string }>('/password/forgot', { email });
      // Only present under the console mail driver, which production forbids.
      setDevCode(result.dev_code ?? null);
      setStep('reset');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not send the code. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function reset(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.post('/password/reset', { email, code, password });
      setStep('done');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not reset the password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-12">
      <div className="w-full max-w-panel">
        <div className="mb-8 flex items-center gap-2.5">
          <LogoMark size={52} />
          <div className="leading-none">
            <p className="text-[15px] font-semibold tracking-tight">RiskPay</p>
            <p className="mt-1 text-2xs text-content-subtle">Merchant Portal</p>
          </div>
        </div>

        <div className="panel p-6">
          {step === 'request' && (
            <>
              <h1 className="text-lg font-semibold tracking-tight">Reset your password</h1>
              <p className="mt-1.5 text-xs leading-relaxed text-content-muted">
                We'll email a one-time code. Your current password stays active until you set a new one.
              </p>
              <form onSubmit={request} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="email" className="label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                  />
                </div>
                {error && (
                  <p role="alert" className="rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-danger-text">
                    {error}
                  </p>
                )}
                <button type="submit" className="btn btn-primary w-full" disabled={busy}>
                  {busy ? <Spinner /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                  Send reset code
                </button>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft ring-1 ring-inset ring-brand-ring">
                <MailCheck className="h-4 w-4 text-brand-400" aria-hidden="true" />
              </span>
              <h1 className="mt-5 text-lg font-semibold tracking-tight">Check your email</h1>
              <p className="mt-1.5 text-xs leading-relaxed text-content-muted">
                If <span className="text-content">{email}</span> has an account, a code is on its way. It expires in 10
                minutes.
              </p>

              {devCode && (
                <p className="mt-4 rounded border border-line bg-surface-inset px-3 py-2.5 text-2xs text-content-subtle">
                  Development mail driver — the code is{' '}
                  <span className="numeral text-brand-400">{devCode}</span>. This never appears in production.
                </p>
              )}

              <form onSubmit={reset} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="code" className="label">
                    Reset code
                  </label>
                  <input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="input numeral tracking-[0.4em]"
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="new-password" className="label">
                    New password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                  />
                  <ul className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {checks.map((c) => (
                      <li
                        key={c.label}
                        className={`flex items-center gap-1.5 text-2xs ${c.ok ? 'text-brand-400' : 'text-content-faint'}`}
                      >
                        <Check className={`h-3 w-3 shrink-0 ${c.ok ? '' : 'opacity-30'}`} aria-hidden="true" />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
                {error && (
                  <p role="alert" className="rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-danger-text">
                    {error}
                  </p>
                )}
                <button type="submit" className="btn btn-primary w-full" disabled={busy || !passwordOk || code.length < 4}>
                  {busy ? <Spinner /> : <Check className="h-4 w-4" aria-hidden="true" />}
                  Set new password
                </button>
              </form>
            </>
          )}

          {step === 'done' && (
            <>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft ring-1 ring-inset ring-brand-ring">
                <Check className="h-4 w-4 text-brand-400" aria-hidden="true" />
              </span>
              <h1 className="mt-5 text-lg font-semibold tracking-tight">Password updated</h1>
              <p className="mt-1.5 text-xs leading-relaxed text-content-muted">
                Sign in with your new password. Any other sessions stay signed in until they expire.
              </p>
              <button type="button" onClick={() => onNavigate('signin')} className="btn btn-primary mt-6 w-full">
                Sign in
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}

          {step !== 'done' && (
            <div className="mt-6 border-t border-line pt-4">
              <button type="button" onClick={() => onNavigate('signin')} className="btn btn-ghost btn-sm -ml-2">
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
